import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  LucideIcon,
  SunMedium,
  Wind,
  Cpu,
  Landmark,
  GraduationCap,
  Activity,
  Wrench,
  BellRing,
  BarChart3,
} from "lucide-react";

interface Solution {
  icon: LucideIcon;
  title: string;
  category: string;
  description: string;
  href: string;
}

interface SolutionsMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

const SolutionsMenu = ({ isOpen, onClose }: SolutionsMenuProps) => {
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isOpen, onClose]);

  // UPDATED: Your real business solutions only
  const solutions: Solution[] = [
    // {
    //   icon: SunMedium,
    //   title: "Solar Monitoring Platform",
    //   category: "Renewable Energy",
    //   description:
    //     "Predictive remote solar plant monitoring with OPC UA integration, anomaly detection, and maintenance intelligence.",
    //   href: "/solutions/solar",
    // },
    // {
    //   icon: Wind,
    //   title: "Industrial IoT / Wind Integration",
    //   category: "Industrial IoT",
    //   description:
    //     "High-throughput OPC UA + Kafka architecture for real-time industrial telemetry, self-healing infrastructure, and open integration.",
    //   href: "/solutions/wind",
    // },
    {
      icon: Activity,
      title: "Solar Performance AI Agent",
      category: "Performance AI Agent",
      description: "Optimize performance, predict failures, and maximize energy generation on a futuristic dashboard.",
      href: "/solutions/solar-performance",
    },
    {
      icon: Wrench,
      title: "Solar Maintenance AI Agent",
      category: "Maintenance AI Agent",
      description: "Predictive alerts and AI health scores to keep solar panels operating at peak reliability.",
      href: "/solutions/solar-maintenance",
    },
    {
      icon: BellRing,
      title: "Solar Alarm Intelligence Agent",
      category: "Alarm Intelligence Agent",
      description: "Automate alarm management and prioritize critical issues in your control center.",
      href: "/solutions/solar-alarm",
    },
    {
      icon: BarChart3,
      title: "Solar Reporting AI Agent",
      category: "Reporting AI Agent",
      description: "Generate comprehensive portfolio analytics and ROI reports instantly.",
      href: "/solutions/solar-reporting",
    },
    {
      icon: Cpu,
      title: "Multi-Vendor Motor Synchronization",
      category: "Industrial Automation",
      description:
        "Unified OPC UA + TSN control layer for deterministic synchronization across multi-OEM motor ecosystems.",
      href: "/solutions/motor",
    },
    {
      icon: Landmark,
      title: "BFSI Microservices Modernization",
      category: "BFSI / FinTech",
      description:
        "Kafka-driven microservices, ElasticSearch tiering, and DC/DR failover for zero-downtime financial modernization.",
      href: "/solutions/bfsi",
    },
    {
      icon: GraduationCap,
      title: "EdTech Child-Safe Talent Platform",
      category: "EdTech / Mobile App",
      description:
        "Privacy-first Flutter platform for under-18 talent showcase, competitions, parental controls, and cognitive development.",
      href: "/solutions/edtech",
    },
  ];

  const handleSolutionClick = (solution: Solution) => {
    navigate(solution.href);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      ref={menuRef}
      className="fixed top-14 left-0 right-0 bg-background/95 backdrop-blur-md border-b border-border shadow-2xl z-[55]"
      onMouseLeave={onClose}
    >
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <h3 className="text-lg font-semibold text-foreground mb-6">
          Our Solutions
        </h3>

        {/* Mobile Layout */}
        <div className="block md:hidden">
          <div className="grid grid-cols-1 gap-4 max-h-[60vh] overflow-y-auto">
            {solutions.map((solution, index) => {
              const isPrimary = index % 2 === 0;
              const Icon = solution.icon;
              return (
                <button
                  key={index}
                  onClick={() => handleSolutionClick(solution)}
                  className={`w-full group text-left px-3 py-3 text-sm text-foreground ${
                    isPrimary
                      ? "hover:text-primary hover:bg-primary/5 hover:border-primary/20"
                      : "hover:text-secondary hover:bg-secondary/5 hover:border-secondary/20"
                  } transition-all duration-200 rounded-md border border-transparent flex items-center gap-3`}
                >
                  <div
                    className={`flex h-8 w-8 items-center justify-center rounded-md ${
                      isPrimary ? "bg-primary/10" : "bg-secondary/10"
                    } flex-shrink-0`}
                  >
                    <Icon
                      className={`h-4 w-4 ${
                        isPrimary
                          ? "text-primary shadow-glow"
                          : "text-secondary"
                      }`}
                    />
                  </div>

                  <div
                    className={`font-medium text-sm leading-tight ${
                      isPrimary
                        ? "group-hover:text-primary"
                        : "group-hover:text-secondary"
                    } transition-colors`}
                  >
                    {solution.title}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
          {solutions.map((solution, index) => {
            const isPrimary = index % 2 === 0;
            const Icon = solution.icon;
            return (
              <div key={index} className="h-full flex flex-col">
                <button
                  onClick={() => handleSolutionClick(solution)}
                  className={`w-full group text-left px-3 py-3 text-sm text-foreground ${
                    isPrimary
                      ? "hover:text-primary hover:bg-primary/5 hover:border-primary/20"
                      : "hover:text-secondary hover:bg-secondary/5 hover:border-secondary/20"
                  } transition-all duration-200 rounded-md border border-transparent flex items-center gap-3`}
                >
                  <div
                    className={`flex h-8 w-8 items-center justify-center rounded-md flex-shrink-0 ${
                      isPrimary ? "bg-primary/10" : "bg-secondary/10"
                    }`}
                  >
                    <Icon
                      className={`h-4 w-4 ${
                        isPrimary
                          ? "text-primary shadow-glow"
                          : "text-secondary"
                      }`}
                    />
                  </div>

                  <div
                    className={`font-medium text-sm leading-tight ${
                      isPrimary
                        ? "group-hover:text-primary"
                        : "group-hover:text-secondary"
                    } transition-colors`}
                  >
                    {solution.title}
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SolutionsMenu;
